// ABOUTME: Shared frame for custom page widgets: a SectionHeader title plus the widget content container.
// ABOUTME: The content container owns the configured background, text color, rounded corners and max height.

import { Box, SectionHeader, Stack } from '@ds/coolshop';
import { ReactNode } from 'react';

import { parseWidgetFrameConfig } from '../types';

// Only the content container is rounded and painted: the title sits outside of it, so a
// configured bgColor never paints behind the header.
const CONTENT_BORDER_RADIUS = '8px';

// Table widgets would otherwise grow with their page size and push everything below them
// off the dashboard, so they are given a fixed height unless the widget config sets its own.
export const TABLE_WIDGET_MAX_HEIGHT = '400px';

interface WidgetFrameProps {
    config?: Record<string, unknown>;
    // Rendered on the right of the section header, e.g. the "View all" and "New" buttons.
    endAdornment?: ReactNode;
    // Height the content takes when the widget config does not set `maxHeight`.
    defaultMaxHeight?: string;
    // Lets the content stretch to the height of its layout column.
    fullHeight?: boolean;
    // Stops the frame from clipping its content. Set by the table widgets: the data table
    // scrolls inside its own container, and its column filter dropdowns and column manager
    // popovers have to be able to paint outside the widget box.
    overflowVisible?: boolean;
    children: ReactNode;
}

export function WidgetFrame({
    config,
    endAdornment,
    defaultMaxHeight,
    fullHeight,
    overflowVisible,
    children,
}: WidgetFrameProps) {
    const { title, bgColor, textColor, maxHeight } = parseWidgetFrameConfig(config);
    const contentHeight = maxHeight ?? defaultMaxHeight;

    return (
        <Stack gap={1} width="100%" minWidth={0} height={fullHeight ? '100%' : undefined}>
            {title && <SectionHeader label={title} endAdornment={endAdornment} divider={false} />}
            {!title && endAdornment && (
                <Box display="flex" justifyContent="flex-end">
                    {endAdornment}
                </Box>
            )}
            {/* A bounded widget always claims its full allowance instead of shrinking to its
                content, so a short list keeps the same footprint as a long one and the widgets
                sharing its row stay aligned. */}
            <Box
                bgcolor={bgColor}
                color={textColor}
                borderRadius={CONTENT_BORDER_RADIUS}
                height={contentHeight ?? (fullHeight ? '100%' : undefined)}
                overflow={overflowVisible ? 'visible' : undefined}
                overflowY={!overflowVisible && contentHeight ? 'auto' : undefined}
                minHeight={0}
            >
                {children}
            </Box>
        </Stack>
    );
}
